import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S05P02({ reveal }: ISlideObject) {
  useEffect(() => {
    window.addEventListener("offline", () => {
      Toastify({
        text: "Parece que estás offline 😢",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top", // `top` or `bottom`
        position: "left", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          background: "linear-gradient(to right,rgb(176, 0, 67),rgb(57, 2, 2))",
        },
        onClick: function () {}, // Callback after click
      }).showToast();
    });
    window.addEventListener("online", () => {
      Toastify({
        text: "Vuelves a estar online 😃",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top", // `top` or `bottom`
        position: "left", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          background: "linear-gradient(to right, #00b09b, #96c93d)",
        },
        onClick: function () {}, // Callback after click
      }).showToast();
    });
    const parent = document.querySelector("#parent");
    const child = document.querySelector("#child");
    // @ts-ignore
    parent.addEventListener("click", () => {
      console.log("Parent clicked");
    });
    // @ts-ignore
    child.addEventListener("click", () => {
      console.log("Child clicked");
    });
  }, []);

  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 05 Parte 2"
      />
      <DefaultSlide title="Agenda">
        <div>
          <ul>
            <li>Repaso JavaScript.</li>
            <li>JavaScript Parte 2</li>
          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide
          title="Repaso de JS"
          subtitle="Preguntas sobre la clase pasada"
        />
        <DefaultSlide title="Sobre motores de JavaScript">
          <div>
            <p>¿Qué es V8 y por qué es relevante para nosotros?</p>
            <StudentPicker number={1} />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Sobre generalidades de JavaScript">
          <div>
            <p>¿Qué son los eventos de JS?</p>
            <StudentPicker number={1} />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Sobre generalidades de JavaScript">
          <div>
            <p>
              ¿Cuál es la diferencia entre <code>let</code>, <code>var</code> y{" "}
              <code>const</code> para definir variables?
            </p>
            <StudentPicker number={1} />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Sobre generalidades de JavaScript">
          <div>
            <p>¿Cuáles son ejemplos de datos primitivos en JavaScript?</p>
            <StudentPicker number={1} />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Sobre generalidades de JavaScript">
          <div>
            <p>
              ¿Cuál es la diferencia entre el iterador <code>for..of</code> y{" "}
              <code>for..in</code>?
            </p>
            <StudentPicker number={1} />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Sobre generalidades de JavaScript">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>If...Else</li>
              <li>Operador ternario</li>
              <li>Switch</li>
              <li>While</li>
              <li>Do..While</li>
              <li>For</li>
              <li>For..in</li>
              <li>For..of</li>
              <li>Try..catch</li>
            </ul>
          </div>
        </DefaultSlide>
      </section>
      <section>
        <TitleSlide title="JavaScript" subtitle="Continuación" />
        <section>
          <p>
            Hoy vamos a ver conceptos más avanzados de JavaScript. Prestar mucha
            atención y si tienen consultas por favor interrumpir. Esta clase es
            clave en el progreso del resto del curso.
          </p>
        </section>
        <DefaultSlide title="La palabra clave 'this' en JavaScript">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La palabra clave <code>this</code> en JavaScript se refiere al
              objeto que está ejecutando el código actual. Su valor depende de
              cómo se invoque la función.
            </p>
            <p>Ejemplos:</p>
            <pre>
              <code>
                {`const obj = {
  name: "Objeto",
  getName: function () {
    return this.name;
  },
};

console.log(obj.getName()); // Salida: "Objeto"

const getName = obj.getName;
console.log(getName()); // Salida: undefined (en modo estricto)`}
              </code>
            </pre>
            <p>
              Nota: En funciones flecha, <code>this</code> no se vincula al
              contexto de la función, sino al contexto léxico donde fue
              definida.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Funciones y el atributo arguments">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El atributo <code>arguments</code> es un objeto disponible dentro
              de todas las funciones no flecha en JavaScript. Contiene una lista
              de los argumentos pasados a la función.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
                {`function suma() {
  let total = 0;
  for (let i = 0; i < arguments.length; i++) {
    total += arguments[i];
  }
  return total;
}

console.log(suma(1, 2, 3, 4)); // Salida: 10`}
              </code>
            </pre>
            <p>
              Nota: En funciones flecha, <code>arguments</code> no está
              disponible. En su lugar, puedes usar el operador <code>rest</code>
              .
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Funciones Flecha">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Las funciones flecha son una forma más concisa de escribir
              funciones en JavaScript. No tienen su propio <code>this</code>,{" "}
              <code>arguments</code>,<code>super</code>, ni{" "}
              <code>new.target</code>.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
                {`const suma = (a, b) => a + b;

console.log(suma(2, 3)); // Salida: 5`}
              </code>
            </pre>
            <p>
              Nota: Las funciones flecha son ideales para callbacks y funciones
              anónimas, pero no deben usarse como métodos de objetos si
              necesitas acceder a <code>this</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Más sobre Funciones Flecha">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: "-30px" }}>
              Las funciones flecha no tienen su propio <code>this</code>. En su
              lugar, heredan el <code>this</code> del contexto en el que fueron
              definidas.
            </p>
            <p>Esto las hace especialmente útiles en métodos como:</p>
            <pre>
              <code>
                {`function Timer() {
  this.seconds = 0;
  setInterval(() => {
    this.seconds++;
    console.log(this.seconds);
  }, 1000);
}

const timer = new Timer();`}
              </code>
            </pre>
            <p>
              En este ejemplo, el <code>this</code> dentro de la función flecha
              se refiere al objeto <code>Timer</code>, no al contexto del{" "}
              <code>setInterval</code>.
            </p>
            <p>
              Sin embargo, esto también significa que no puedes cambiar el valor
              de <code>this</code> usando métodos como <code>call</code> o{" "}
              <code>apply</code>.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Sobre NaN (Not-a-Number)">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              <code>NaN</code> significa "Not-a-Number" y es un valor especial
              en JavaScript que indica que una operación matemática no produjo
              un número válido.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
                {`console.log(0 / 0); // Salida: NaN
console.log(parseInt("abc")); // Salida: NaN`}
              </code>
            </pre>
            <p>
              Nota: <code>NaN</code> es de tipo <code>number</code>, pero no es
              igual a ningún valor, ni siquiera a sí mismo.
            </p>
            <p>
              Para verificar si un valor es <code>NaN</code>, usa{" "}
              <code>isNaN()</code>:
            </p>
            <pre>
              <code>
                {`console.log(isNaN(NaN)); // Salida: true
console.log(isNaN("abc")); // Salida: true`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Métodos call y apply">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: "-30px" }}>
              Los métodos <code>call</code> y <code>apply</code> en JavaScript
              se utilizan para invocar funciones y establecer explícitamente el
              valor de <code>this</code>.
            </p>
            <p style={{ margin: "0" }}>
              Ejemplo con <code>call</code>:
            </p>
            <pre style={{ margin: "0" }}>
              <code>
                {`function saludar(saludo) {
        console.log(saludo + ", " + this.nombre);
}

const persona = { nombre: "Juan" };

saludar.call(persona, "Hola"); // Salida: "Hola, Juan"`}
              </code>
            </pre>
            <p style={{ marginBottom: "0" }}>
              Ejemplo con <code>apply</code>:
            </p>
            <pre style={{ margin: "0" }}>
              <code>
                {`function sumar(a, b) {
  return a + b;
}

console.log(sumar.apply(null, [2, 3])); // Salida: 5
console.log(sumar.call(null, 2, 3)); // Salida: 5`}
              </code>
            </pre>
            <p>
              Nota: La diferencia principal entre <code>call</code> y{" "}
              <code>apply</code> es cómo se pasan los argumentos:{" "}
              <code>call</code> los recibe como una lista, mientras que{" "}
              <code>apply</code> los recibe como un arreglo.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="setTimeout en JavaScript">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: "-30px" }}>
              <code>setTimeout</code> es una función en JavaScript que ejecuta
              un código o función después de un período de tiempo especificado.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
                {`console.log("Inicio");

setTimeout(() => {
  console.log("Esto se ejecuta después de 2 segundos");
}, 2000);

console.log("Fin");`}
              </code>
            </pre>
            <p>
              En este ejemplo, el mensaje "Esto se ejecuta después de 2
              segundos" se muestra después de que hayan pasado 2 segundos,
              mientras que "Inicio" y "Fin" se muestran inmediatamente.
            </p>
            <p>
              Nota: <code>setTimeout</code> no detiene la ejecución del resto
              del código. Es una función <strong>asíncrona</strong>.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            En unos momentos hablaremos de asincronicidad y la forma en que
            JavaScript maneja el Event Loop
          </p>
          <p>Sigamos viendo algunos conceptos más avanzados de eventos.</p>
        </section>
        <DefaultSlide title="Eventos Inline vs Registro de Eventos">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              En JavaScript, hay dos formas principales de manejar eventos:
              usando eventos inline o registrando eventos con JavaScript.
            </p>
            <p>Ejemplo de eventos inline:</p>
            <pre>
              <code>
                {`<button onclick="alert('Hola!')">Haz clic aquí</button>`}
              </code>
            </pre>
            <p>
              En este caso, el evento está directamente en el HTML, lo que puede
              hacer que el código sea más difícil de mantener.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            De hecho en la clase pasada utilizamos un registro de evento para
            capturar el evento "offline" y "online".
          </p>
        </section>
        <DefaultSlide title="Eventos Inline vs Registro de Eventos">
          <div style={{ fontSize: "0.75em" }}>
            <p>Ejemplo de registro de eventos:</p>
            <pre>
              <code>
                {`const button = document.querySelector("button");
button.addEventListener("click", () => {
  alert("Hola!");
});`}
              </code>
            </pre>
            <p>
              En este caso, el evento se registra en JavaScript, lo que permite
              separar la lógica del comportamiento y el HTML. Esto hace que el
              código sea más limpio y fácil de mantener.
            </p>
            <p>
              Además, se pueden registrar múltiples eventos en un solo elemento
              y eliminar eventos si es necesario.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Eliminando el registro de eventos">
          <div style={{ fontSize: "0.75em" }}>
            <p>Ejemplo de eliminar un evento:</p>
            <pre>
              <code>
                {`const button = document.querySelector("button");
const handleClick = () => {
  alert("Hola!");
}
button.addEventListener("click", handleClick);
button.removeEventListener("click", handleClick);`}
              </code>
            </pre>
            <p>
              En este caso, el evento de clic se elimina después de que se
              registra, lo que significa que no se ejecutará cuando se haga clic
              en el botón.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>¿Qué pasa si tengo elementos anidados con eventos?</p>
          <pre>
            <code className="language-html">
              {`<div id="parent">
  <button id="child">Haz clic aquí</button>
</div>
`}
            </code>
          </pre>
          <pre>
            <code className="language-js">
              {`const parent = document.querySelector("#parent");
const child = document.querySelector("#child");
parent.addEventListener("click", () => {
  console.log("Parent clicked");
});
child.addEventListener("click", () => {
  console.log("Child clicked");
});
`}
            </code>
          </pre>
          <div
            id="parent"
            style={{
              width: "200px",
              height: "200px",
              backgroundColor: "lightblue",
            }}
          >
            <button id="child">Child button!</button>
          </div>
        </section>
        <DefaultSlide title="Propagación de Eventos">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: "-30px" }}>
              La propagación de eventos en JavaScript describe cómo los eventos
              se mueven a través del DOM en dos fases principales: la fase de
              burbuja y la fase de captura.
            </p>
            <p>Ejemplo de fase de burbuja:</p>
            <pre>
              <code>
                {`document.querySelector("#child").addEventListener("click", () => {
  console.log("Child clicked");
});

document.querySelector("#parent").addEventListener("click", () => {
  console.log("Parent clicked");
});`}
              </code>
            </pre>
            <p>
              Si haces clic en el elemento hijo, primero se ejecutará el evento
              del hijo y luego el del padre.
            </p>
          </div>
        </DefaultSlide>

        <section>
          <p>¿Esto es estándar para todos los navegadores?</p>
          <img
            className="fragment"
            src="/images/S05/iememe.jpg"
            alt="IE meme"
            width={500}
          />
          <p className="fragment">Lamentablemente no...</p>
        </section>

        <DefaultSlide title="Fase de Burbuja y Fase de Captura">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La propagación de eventos en JavaScript tiene dos fases
              principales:
              <strong> la fase de captura</strong> y{" "}
              <strong>la fase de burbuja</strong>.
            </p>
            <p>
              1. <strong>Fase de captura:</strong> El evento se propaga desde el
              elemento raíz del DOM hacia el objetivo del evento.
            </p>
            <p>
              2. <strong>Fase de burbuja:</strong> Después de alcanzar el
              objetivo, el evento se propaga de regreso hacia el elemento raíz.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ejemplo con ambas fases">
          <div style={{ fontSize: "0.75em" }}>
            <pre>
              <code className="language-js">
                {`document.querySelector("#child").addEventListener(
  "click",
  () => {
    console.log("Child clicked (bubble)");
  },
  false // false indica la fase de burbuja
);

document.querySelector("#parent").addEventListener(
  "click",
  () => {
    console.log("Parent clicked (capture)");
  },
  true // true indica la fase de captura
);`}
              </code>
            </pre>
            <p>
              En este ejemplo, si haces clic en el elemento hijo, primero se
              ejecutará el evento del padre en la fase de captura y luego el del
              hijo en la fase de burbuja.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Detener la Propagación de Eventos">
          <div>
            <p>¿Y si deseamos que la propagación se detenga?</p>
            <pre>
              <code>
                {`document.querySelector("#child").addEventListener("click", (e) => {
          e.stopPropagation();
          console.log("Child clicked");
        });`}
              </code>
            </pre>
            <p>
              En este caso, el evento del padre no se ejecutará porque la
              propagación fue detenida.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>Ahora veamos una buena práctica en JS</p>
        </section>
        <DefaultSlide title="Delegación de Eventos">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: "-30px" }}>
              La delegación de eventos es una técnica que permite manejar
              eventos en un elemento padre en lugar de en cada elemento hijo
              individualmente.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
                {`const list = document.querySelector("#list");
list.addEventListener("click", (e) => {
  if (e.target.tagName === "LI") {
    console.log("List item clicked:", e.target.textContent);
  }
});`}
              </code>
            </pre>
            <p>
              En este caso, el evento de clic se maneja en el elemento padre
              <code>#list</code>, y se verifica si el elemento clicado es un
              elemento <code>&lt;li&gt;</code>. Esto es útil cuando se tienen
              muchos elementos hijos y se quiere evitar registrar eventos en
              cada uno de ellos.
            </p>
            <p>
              La delegación de eventos también mejora el rendimiento, ya que se
              reduce la cantidad de manejadores de eventos registrados en el
              DOM.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>¿Notaron el e.target.textContext?</p>
          <p className="fragment">¿De dónde sale este objeto?</p>
        </section>
        <DefaultSlide title="El objeto Event">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El objeto <code>Event</code> es un objeto que se pasa
              automáticamente a los manejadores de eventos en JavaScript.
              Contiene información sobre el evento que ocurrió.
            </p>
            <p>Ejemplo:</p>
            <pre>
              <code>
                {`document.querySelector("#testEvent").addEventListener("click", (e) => {
  console.log("Evento:", e);
  console.log("Tipo de evento:", e.type);
  console.log("Elemento objetivo:", e.target);
  console.log("Coordenadas del clic:", e.clientX, e.clientY);
});`}
              </code>
            </pre>
            <p>
              En este ejemplo, el objeto <code>e</code> es el objeto de evento
              que contiene información sobre el clic en el elemento hijo.
            </p>
            <button id="testEvent">Botón con ID testEvent</button>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Ahora hagamos énfasis en algo que hemos utilizado a lo largo de las
            últimas diapositivas:
          </p>
          <pre>
            <code className="language-js">
              {`document.querySelector("#parent").addEventListener("click", () => {});
document.querySelector("#parent").addEventListener(EVENT_NAME, CALLBACK_FUNCTION);`}
            </code>
          </pre>
        </section>
        <DefaultSlide title="Funciones Callback">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Una función <strong>callback</strong> es una función que se pasa
              como argumento a otra función y se ejecuta después de que la
              función principal haya terminado su tarea.
            </p>
            <pre>
              <code>
                {`function procesarUsuario(nombre, callback) {
  console.log("Procesando usuario:", nombre);
  callback();
}

procesarUsuario("Juan", () => {
  console.log("Usuario procesado.");
});`}
              </code>
            </pre>
            <p>
              En este ejemplo, la función <code>callback</code> se ejecuta
              después de que se procesa el usuario.
            </p>
            <p>
              Las funciones callback son muy comunes en JavaScript,
              especialmente en operaciones asíncronas como eventos,
              temporizadores y solicitudes HTTP.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Funciones Callback">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La función callback no siempre necesitamos que tenga un nombre,
              podemos pasarle una función anónima como argumento.
            </p>
            <pre>
              <code>
                {`function queryFromDb(ID, callback) {
  setTimeout(() => {
    console.log("Usuario encontrado en la base de datos con ID:", ID);
    // Simulando una consulta a la base de datos
    // Después de 2 segundos, se ejecuta la función callback
    callback();
  }, 2000);
}
queryFromDb(1, () => {
  console.log("Consulta terminada.");
});`}
              </code>
            </pre>
            <p>¿Qué pasaría si el callback estuviera afuera?</p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Por qué necesitamos funciones Callback?">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Las funciones callback son esenciales en JavaScript porque
              permiten manejar tareas asíncronas de manera eficiente. JavaScript
              es un lenguaje de un solo hilo, lo que significa que solo puede
              ejecutar una tarea a la vez.
            </p>
            <p>
              Sin funciones callback, el código asíncrono podría bloquear la
              ejecución de otras tareas, haciendo que la aplicación sea menos
              receptiva.
            </p>
            <p>Ejemplo sin callback:</p>
            <pre>
              <code>
                {`function tareaPesada() {
  for (let i = 0; i < 10e9; i++) {} // Simula una tarea pesada
  console.log("Tarea pesada completada");
}

console.log("Inicio");
tareaPesada();
console.log("Fin");`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Por qué necesitamos funciones Callback?">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              En este caso, la tarea pesada bloquea la ejecución del resto del
              código.
            </p>
            <p>Ejemplo con callback:</p>
            <pre>
              <code className="language-js">
                {`console.log("Inicio");

setTimeout(() => {
  console.log("Tarea pesada completada");
}, 1000);

console.log("Fin");`}
              </code>
            </pre>
            <p>
              Aquí, la tarea pesada se ejecuta de manera asíncrona, permitiendo
              que el resto del código continúe ejecutándose sin interrupciones.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>Pueden existir muchos procesos que simulan ese setTimeout:</p>
          <ul>
            <li>Llamada a un servidor.</li>
            <li>Lectura de un documento muy grande.</li>
            <li>Un usuario interactuando con una UI.</li>
          </ul>
          <p>
            ¡Genial! Ya tenemos asincronicidad en JS, por lo que podemos seguir
            tranquilamente agregando callbacks...
          </p>
        </section>
        <section>
          <img src="/images/S05/callback.jpg" alt="Callbacks" />
        </section>
        <DefaultSlide title="Callback Hell">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El término <strong>Callback Hell</strong> se refiere a la
              situación en la que múltiples funciones callback están anidadas
              unas dentro de otras, haciendo que el código sea difícil de leer y
              mantener.
            </p>
            <pre>
              <code className="language-js" style={{ height: "200px" }}>
                {`function firstTask(callback) {
  setTimeout(() => {
    console.log("Primera tarea completada");
    callback();
  }, 1000);
}

function secondTask(callback) {
  setTimeout(() => {
    console.log("Segunda tarea completada");
    callback();
  }, 1000);
}

function thirdTask(callback) {
  setTimeout(() => {
    console.log("Tercera tarea completada");
    callback();
  }, 1000);
}

firstTask(() => {
  secondTask(() => {
    thirdTask(() => {
      console.log("Todas las tareas completadas");
    });
  });
});`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Este tipo de código puede volverse inmanejable rápidamente a medida
            que aumenta la complejidad.
          </p>
          <img
            src="/images/S05/callbackhell.png"
            alt="callback hell example"
            width={500}
          />
          <p>
            Para evitar el Callback Hell, se pueden usar técnicas como Promesas
            o Async/Await.
          </p>
        </section>
        <section>
          <p>
            Pronto veremos más sobre promesas y async/await, pero primero vamos
            a entrar en el mundo del Event Loop.
          </p>
        </section>
      </section>
      <section>
        <TitleSlide title="JavaScript y el Event Loop" />
        <section>
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/FSs_JYwnAdI?si=Loyk9cj7EC0bAAeU"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </section>
        <section>
          <p>
            El Event Loop es el mecanismo que permite a JavaScript manejar la
            asincronía y los eventos de manera eficiente. Permite que JavaScript
            ejecute código, recolecte y procese eventos y ejecute tareas en
            segundo plano.
          </p>
        </section>
        <DefaultSlide title="El Event Loop en Detalle">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El <strong>Event Loop</strong> es un componente fundamental en el
              modelo de concurrencia de JavaScript. Permite que el lenguaje
              maneje tareas asíncronas y eventos de manera eficiente, a pesar de
              ser de un solo hilo.
            </p>
            <p>
              El Event Loop funciona en conjunto con la{" "}
              <strong>pila de llamadas</strong> (Call Stack), la{" "}
              <strong>cola de tareas</strong> (Task Queue) y la{" "}
              <strong>cola de microtareas</strong> (Microtask Queue).
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El Event Loop en Detalle">
          <div style={{ fontSize: "0.75em" }}>
            <p>Flujo básico del Event Loop:</p>
            <ol>
              <li>
                JavaScript ejecuta las funciones en la{" "}
                <strong>pila de llamadas</strong>.
              </li>
              <li>
                Cuando una tarea asíncrona se completa, su callback se coloca en
                la <strong>cola de tareas</strong> o en la{" "}
                <strong>cola de microtareas</strong>, dependiendo del tipo de
                tarea.
              </li>
              <li>
                El Event Loop verifica si la pila de llamadas está vacía. Si lo
                está, toma la primera tarea de la cola de microtareas y la
                ejecuta.
              </li>
              <li>
                Si no hay microtareas pendientes, el Event Loop toma la primera
                tarea de la cola de tareas y la ejecuta.
              </li>
            </ol>
          </div>
        </DefaultSlide>
        <DefaultSlide title="El Event Loop en Detalle">
          <div style={{ fontSize: "0.75em" }}>
            <p style={{ marginTop: "-30px" }}>Ejemplo de prioridades:</p>
            <pre>
              <code>
                {`console.log("Inicio");

setTimeout(() => {
  console.log("Tarea de la cola de tareas");
}, 0);

Promise.resolve().then(() => {
  console.log("Tarea de la cola de microtareas");
});

console.log("Fin");`}
              </code>
            </pre>
            <p>Salida:</p>
            <pre>
              <code>
                {`Inicio
        Fin
        Tarea de la cola de microtareas
        Tarea de la cola de tareas`}
              </code>
            </pre>
            <p>
              En este ejemplo, las microtareas (como las promesas) tienen mayor
              prioridad que las tareas normales (como <code>setTimeout</code>).
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>¿Pero qué son las promesas?</p>
        </section>
      </section>
      <section>
        <TitleSlide title="Promesas en JavaScript" />
        <DefaultSlide title="Introducción a Promesas">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Una <strong>promesa</strong> en JavaScript es un objeto que
              representa la eventual finalización (o falla) de una operación
              asíncrona y su valor resultante.
            </p>
            <p>Estados de una promesa:</p>
            <ul>
              <li>
                <strong>Pendiente (Pending):</strong> La operación aún no se ha
                completado.
              </li>
              <li>
                <strong>Resuelta (Fulfilled):</strong> La operación se completó
                con éxito.
              </li>
              <li>
                <strong>Rechazada (Rejected):</strong> La operación falló.
              </li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Creando una Promesa">
          <div style={{ fontSize: "0.75em" }}>
            <p>Ejemplo básico:</p>
            <pre>
              <code>
                {`const promesa = new Promise((resolve, reject) => {
          const exito = true;
          if (exito) {
            resolve("Operación exitosa");
          } else {
            reject("Operación fallida");
          }
        });

        promesa
          .then((resultado) => {
            console.log(resultado); // "Operación exitosa"
          })
          .catch((error) => {
            console.error(error); // "Operación fallida"
          });`}
              </code>
            </pre>
            <p>
              Las promesas son útiles para manejar operaciones asíncronas de
              manera más legible y estructurada.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Encadenando Promesas">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Se puede encadenar múltiples promesas usando el método{" "}
              <code>then</code>.
            </p>
            <pre>
              <code>
                {`const promesa1 = new Promise((resolve) => {
  setTimeout(() => {
    resolve("Promesa 1 resuelta");
  }
, 1000);
}
);
const promesa2 = new Promise((resolve) => {
  setTimeout(() => {
    resolve("Promesa 2 resuelta");
  }
, 2000);
}
);
promesa1
  .then((resultado) => {
    console.log(resultado);
    return promesa2;
  })
  .then((resultado) => {
    console.log(resultado);
  })
  .catch((error) => {
    console.error("Error:", error);
  });`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="async-await">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              <strong>async</strong> y <strong>await</strong> son palabras clave
              en JavaScript que permiten trabajar con promesas de manera más
              sencilla y legible.
            </p>
            <p>
              La palabra clave <strong>async</strong> se utiliza para declarar
              una función asíncrona, que siempre devuelve una promesa.
            </p>
            <p>
              La palabra clave <strong>await</strong> se utiliza dentro de una
              función asíncrona para esperar a que una promesa se resuelva o se
              rechace.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="async-await">
          <div style={{ fontSize: "0.75em" }}>
            <p>Ejemplo de uso de async/await:</p>
            <pre>
              <code>
                {`async function obtenerDatos() {
  try {
    const respuesta = await fetch("https://api.example.com/data");
    const datos = await respuesta.json();
    console.log(datos);
  } catch (error) {
    console.error("Error al obtener datos:", error);
  }
}
const datos = obtenerDatos();`}
              </code>
            </pre>
            <p>
              Veamos un ejemplo para traer el valor del dólar en CR.{" "}
              <span>pero antes...</span>
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            ¿Qué es fetch y el método <code>await respuesta.json()</code>?
          </p>
        </section>
        <DefaultSlide title="Fetch API">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Fetch es una API moderna de JavaScript que permite realizar
              solicitudes HTTP de manera sencilla y flexible.
            </p>
            <p>
              El método <code>fetch()</code> devuelve una promesa que se
              resuelve cuando la respuesta está disponible.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Qué es JSON?">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              <a
                href="https://www.json.org/json-en.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                JSON
              </a>{" "}
              (JavaScript Object Notation) es un formato ligero de intercambio
              de datos. Es fácil de leer y escribir para los humanos, y fácil de
              analizar y generar para las máquinas.
            </p>
            <p>
              El método <code>json()</code> convierte la respuesta de la
              solicitud en un objeto JavaScript.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <CodePen penId="QwWrKxG" title="Ejemplo dólar en CR" />
        </section>
      </section>
      <ImageSlide image="/images/S05/dancing5.gif" title="Fin de parte 1" />
    </>
  );
}
